import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import { useDropzone } from 'react-dropzone';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import TableChartIcon from '@material-ui/icons/TableChart';
import LinkIcon from '@material-ui/icons/Link';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';

import InputLabel from 'components/inputfields/InputLabel';
import IProduct, { IProductDocument, ProductDocumentCategoryEnum } from 'models/product';
import COLORS from 'constants/colors';
import { useMutation } from '@apollo/react-hooks';
import { S3_SIGNED_REQUEST_MUTATION } from 'graphql/mutations';
import { TSignedRequest } from '../FillProductByImage';
import { Box } from '@material-ui/core';
import { resize64Image } from 'utils/images-ts';
import { isCloudinary } from 'utils/cloudinary-ts';
import ConfirmModal from 'components/ConfirmModal';
import { removeNilProperty } from 'components/vision/tabs/chatGPT';
import { scrollWithOffset } from '../alerts';
import { getUnsatisfiedDocCategory, PRODUCT_DOCUMENTS_SECTION_ID } from './doc-requirements';
import { KeycloakContext } from 'components/Secured';

export const getNormalizedDocumentUrl = (linkUrl: string) => {
  const trimmed = linkUrl.trim();
  if (!trimmed) {
    return { error: 'Ange en URL' };
  }
  let normalized = trimmed;
  if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;
  try {
    new URL(normalized);
  } catch {
    return { error: 'Ogiltig URL' };
  }
  return { normalized };
}

const MAX_FILE_SIZE = 25 * 1024 * 1024;

const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
].join(', ');

export const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'SDS', label: ProductDocumentCategoryEnum.SDS },
  { value: 'CERTIFICATE', label: ProductDocumentCategoryEnum.CERTIFICATE },
  { value: 'MANUAL', label: ProductDocumentCategoryEnum.MANUAL },
  { value: 'PRODUCT_SHEET', label: ProductDocumentCategoryEnum.PRODUCT_SHEET },
  { value: 'EPREL', label: ProductDocumentCategoryEnum.EPREL },
  { value: 'MARKETING', label: ProductDocumentCategoryEnum.MARKETING },
  { value: 'OTHER', label: ProductDocumentCategoryEnum.OTHER },
  { value: 'BATTERY_PRODUCT_INFO', label: ProductDocumentCategoryEnum.BATTERY_PRODUCT_INFO },
  { value: 'PPWR_DOC', label: ProductDocumentCategoryEnum.PPWR_DOC },
];

const formatBytes = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('sv-SE');
  } catch {
    return '';
  }
};

const getHost = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const renderTypeIcon = (doc: IProductDocument) => {
  if (doc.type === 'link') return <LinkIcon style={{ color: '#2196f3' }} />;
  const m = doc.mimeType ?? '';
  if (m === 'application/pdf') return <PictureAsPdfIcon style={{ color: '#e53935' }} />;
  if (m.includes('spreadsheet') || m.includes('excel') || m === 'text/csv')
    return <TableChartIcon style={{ color: '#43a047' }} />;
  if (m.includes('word')) return <DescriptionIcon style={{ color: '#1e88e5' }} />;
  return <InsertDriveFileIcon style={{ color: '#757575' }} />;
};

// Resolve correct MIME type based on file extension to ensure AWS S3 presigned request
// uses the accurate Content-Type header on file upload and download.
export const getDocMimeType = (fileName: string, mimeType?: string) => {
  if (mimeType) return mimeType;
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'csv':
      return 'text/csv';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'doc':
      return 'application/msword';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'webp':
      return 'image/webp';
    case 'txt':
      return 'text/plain';
    default:
      return 'application/octet-stream';
  }
};

export const useProductDocuments = () => {
  const [s3SignedRequest] = useMutation(S3_SIGNED_REQUEST_MUTATION, { refetchQueries: [] });

  const checkNeedUploadDocument = useCallback((document: IProductDocument) => {
    if (document.type !== 'file') return false;
    if (!document.url.startsWith('blob:')) return false;
    return true;
  }, []);

  const getS3Requests = useCallback(
    async (state: IProduct, documents: IProductDocument[]) => {
      if (!state || !documents.length) return [];

      const { data } = await s3SignedRequest({
        variables: {
          input: {
            isProduct: true,
            ean: String(state.EAN),
            producerName: state.producerName,
            producerUsername: state.producerUsername,
            files: documents.map(({ name, mimeType }) => ({
              fileName: name,
              fileType: getDocMimeType(name, mimeType),
            })),
            renameFiles: true,
          },
        },
      });

      return data.getS3SignRequest?.requests as TSignedRequest[];
    },
    [s3SignedRequest]
  );

  const onUploadProductDocuments = useCallback(
    async (state?: IProduct) => {
      const documents = state?.documents || [];
      let uploadingDocuments = documents.filter(checkNeedUploadDocument);
      const restDocuments = documents.filter(doc => !checkNeedUploadDocument(doc));
      if (!state || !uploadingDocuments.length) return state?.documents;

      const requests = await getS3Requests(state, uploadingDocuments);

      uploadingDocuments = await Promise.all(
        uploadingDocuments.map(async (document, index) => {
          if (!checkNeedUploadDocument(document)) return document;

          const blob = await fetch(document.url || '').then(res => res.blob());
          const fileType = getDocMimeType(document.name, document.mimeType) || blob.type;
          const file = new File([blob], document.name, { type: fileType });
          const signedRequest = requests[index];

          await axios.put(signedRequest.signedRequest, file, {
            timeout: 10 * 60 * 1000,
            headers: { 'Content-Type': file.type },
          });

          return { ...document, url: requests[index].pictureUrl };
        })
      );

      return [...restDocuments, ...uploadingDocuments];
    },
    [getS3Requests, checkNeedUploadDocument]
  );

  return { onUploadProductDocuments };
};

interface DocumentCategorySelectorProps {
  disabled?: boolean;
  value?: string;
  onChange: (value: ProductDocumentCategoryEnum | '') => void;
}

export const DocumentCategorySelector = ({ disabled, value: valueProp, onChange }: DocumentCategorySelectorProps) => {
  const [value, setValue] = useState(valueProp ?? '');

  useEffect(() => {
    setValue(valueProp ?? '');
  }, [valueProp]);

  return (
    <FormControl size="small" variant="outlined" style={{ flexShrink: 0, minWidth: 140 }}>
      <Select
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => {
          onChange(e.target.value as ProductDocumentCategoryEnum | '');
          setValue(e.target.value as ProductDocumentCategoryEnum | '');
        }}
        displayEmpty
        aria-label="Dokumenttyp"
        style={{ fontSize: 13, color: value ? '#424242' : '#9e9e9e', backgroundColor: '#fff' }}
        inputProps={{ style: { padding: '6px 28px 6px 10px' } }}
      >
        <MenuItem value="" style={{ fontSize: 13, color: '#9e9e9e' }}>
          Välj typ
        </MenuItem>
        {CATEGORY_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value} style={{ fontSize: 13 }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

interface IDocumentRowProps {
  product?: IProduct;
  doc: IProductDocument;
  onUpdate: (doc: IProductDocument) => void;
  onRemove: (id: string) => void;
  onView?: (url: string) => void;
  readOnly?: boolean;
  alwayConfirmDelete?: boolean;
}

export const DocumentRow = ({ product, doc, onUpdate, onRemove, onView, readOnly, alwayConfirmDelete }: IDocumentRowProps) => {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(doc.name);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const isImage = doc.type === 'file' && (doc.mimeType ?? '').startsWith('image/');
  const isPdfUrl = doc.type === 'file' && isCloudinary(doc.url) && !!doc.mimeType?.startsWith('application/pdf');
  const isUploaded = !!product?.documents?.some(({ id }) => id === doc.id);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEdit = () => {
    setDraftName(doc.name);
    setEditing(true);
  };

  const commitEdit = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== doc.name) onUpdate({ ...doc, name: trimmed });
    setEditing(false);
  };

  const cancelEdit = () => {
    setDraftName(doc.name);
    setEditing(false);
  };

  const onCategoryChange = (value: ProductDocumentCategoryEnum | '') => {
    onUpdate({ ...doc, category: value === '' ? undefined : (value as ProductDocumentCategoryEnum) });
  };

  const handleRemove = () => {
    if (isUploaded || alwayConfirmDelete) {
      setShowConfirmDelete(true);
    } else {
      onRemove(doc.id);
    }
  };

  const productInfo = product?.title ? `${product.title}${product.EAN ? ` (${product.EAN})` : ''}` : '';
  const secondaryLine =
    doc.type === 'file'
      ? [productInfo, formatBytes(doc.size), formatDate(doc.addedAt)].filter(Boolean).join(' · ')
      : [productInfo, getHost(doc.url), formatDate(doc.addedAt)].filter(Boolean).join(' · ');

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 12px',
        borderBottom: '1px solid #eeeeee',
        gap: 12,
        backgroundColor: hovered ? '#f5f5f5' : 'transparent',
        transition: 'background-color 0.15s',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isImage || isPdfUrl ? '#000' : '#f0f0f0',
          borderRadius: 4,
          overflow: 'hidden',
          cursor: onView ? 'pointer' : '',
        }}
        onClick={() => onView?.(doc.url)}
      >
        {isImage || isPdfUrl ? (
          <img
            src={resize64Image(doc.url)}
            alt={doc.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          renderTypeIcon(doc)
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <input
            ref={inputRef}
            aria-label="Ändra dokumentnamn"
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => {
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            style={{
              width: '100%',
              padding: '4px 6px',
              fontSize: 14,
              fontWeight: 500,
              border: '1px solid #2196f3',
              borderRadius: 3,
              outline: 'none',
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
              title={doc.name}
            >
              {doc.name}
            </span>
            {!readOnly && hovered && (
              <IconButton
                size="small"
                aria-label="Byt namn"
                title="Byt namn"
                onClick={startEdit}
                style={{ padding: 2 }}
              >
                <EditOutlinedIcon style={{ fontSize: 14, color: '#757575' }} />
              </IconButton>
            )}
          </div>
        )}
        {secondaryLine && (
          <div
            style={{
              fontSize: 12,
              color: '#9e9e9e',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={doc.type === 'link' ? doc.url : undefined}
          >
            {secondaryLine}
          </div>
        )}
      </div>

      <DocumentCategorySelector
        disabled={readOnly}
        value={doc.category ?? ''}
        onChange={onCategoryChange}
      />

      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        {!!onView && <IconButton size="small" className="preview-allow-click" aria-label="Visa" title="Visa" onClick={() => onView(doc.url)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>}
        {!readOnly && (
          <IconButton size="small" aria-label="Ta bort" title="Ta bort" onClick={handleRemove}>
            <DeleteOutlineIcon fontSize="small" style={{ color: COLORS.mainRed }} />
          </IconButton>
        )}
      </div>

      <ConfirmModal
        open={showConfirmDelete}
        content={`Vill du ta bort dokumentet "${doc.name}"?`}
        onCancel={() => setShowConfirmDelete(false)}
        onOk={() => onRemove(doc.id)}
        okText="Ta bort"
        cancelText="Avbryt"
      />
    </div>
  );
};

interface ProductDocumentsImportProps {
  state?: IProduct;
  canAddLink?: boolean;
  warnMessage?: ReactNode;
  onAdd: (docs: IProductDocument[]) => void;
}

export const ProductDocumentsImport = ({ state, canAddLink = true, warnMessage, onAdd }: ProductDocumentsImportProps) => {
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [linkError, setLinkError] = useState('');
  const [dropErrors, setDropErrors] = useState<string[]>([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_TYPES,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    onDrop: (accepted: File[], rejected: File[]) => {
      const newDropErrors: string[] = [];
      if (rejected.length) {
        rejected.forEach(file => {
          if (file.size > 25 * 1024 * 1024) {
            newDropErrors.push(`Filen "${file.name}" är för stor (max 25MB)`);
          }
        });
      }
      setDropErrors(newDropErrors);

      if (accepted.length) {
        // If a conditional document requirement is triggered but unsatisfied,
        // default the first uploaded file to its category (e.g. PPWR Ja → PPWR DoC).
        const autoCategory = getUnsatisfiedDocCategory(state);
        const newDocs: IProductDocument[] = accepted.map((file, index) => ({
          id: uuid(),
          type: 'file',
          name: file.name,
          originalFilename: file.name,
          url: URL.createObjectURL(file),
          size: file.size,
          mimeType: file.type,
          addedAt: new Date().toISOString(),
          ...(autoCategory && index === 0 ? { category: autoCategory } : {}),
        }));
        onAdd(newDocs);
      }
    },
  });

  const addLink = () => {
    const { error, normalized = '' } = getNormalizedDocumentUrl(linkUrl);
    if (error) {
      setLinkError(error);
      return;
    }
    const newDoc: IProductDocument = {
      id: uuid(),
      type: 'link',
      name: linkLabel.trim() || normalized,
      originalFilename: linkLabel.trim() || normalized,
      url: normalized,
      addedAt: new Date().toISOString(),
    };
    onAdd([newDoc]);
    setLinkUrl('');
    setLinkLabel('');
    setLinkError('');
    setShowLinkForm(false);
  };

  const cancelLink = () => {
    setLinkUrl('');
    setLinkLabel('');
    setLinkError('');
    setShowLinkForm(false);
  };

  return (
    <>
      <div
        {...getRootProps()}
        aria-label="Ladda upp dokument"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '28px 20px',
          borderWidth: 2,
          borderRadius: 4,
          borderStyle: 'dashed',
          borderColor: isDragActive ? '#2196f3' : '#cfcfcf',
          backgroundColor: isDragActive ? '#eef7ff' : '#fafafa',
          cursor: 'pointer',
          outline: 'none',
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadOutlinedIcon style={{ fontSize: 36, color: '#2196f3', marginBottom: 6 }} />
        <span style={{ fontSize: 14, color: '#424242', fontWeight: 500 }}>
          {isDragActive ? 'Släpp filerna här...' : 'Dra dokument hit eller klicka för att välja från din dator'}
        </span>
        <span style={{ fontSize: 12, color: '#757575', marginTop: 4 }}>
          PDF, bilder, Word, Excel, txt, csv · max 25 MB per fil
        </span>
      </div>

      {!!dropErrors.length && (
        <Box mt={1} color="orangered" fontWeight={700}>
          {dropErrors.map((error, index) => (
            <Box key={`${error}_${index}`}>{error}</Box>
          ))}
        </Box>
      )}

      {!!warnMessage && (
        <Box mt={1} color="orangered" fontWeight={700} fontSize={16}>
          {warnMessage}
        </Box>
      )}

      {canAddLink && <div style={{ marginTop: 12 }}>
        {!showLinkForm ? (
          <Button
            onClick={() => setShowLinkForm(true)}
            color="primary"
            startIcon={<AddIcon />}
            style={{ textTransform: 'none' }}
          >
            Lägg till länk
          </Button>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              padding: 12,
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              backgroundColor: '#fafafa',
            }}
          >
            <input
              type="url"
              value={linkUrl}
              onChange={e => {
                setLinkUrl(e.target.value);
                if (linkError) setLinkError('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') addLink();
                if (e.key === 'Escape') cancelLink();
              }}
              placeholder="https://..."
              autoFocus
              style={{
                flex: '2 1 240px',
                padding: '8px 10px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 14,
              }}
            />
            <input
              type="text"
              value={linkLabel}
              onChange={e => setLinkLabel(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') addLink();
                if (e.key === 'Escape') cancelLink();
              }}
              placeholder="Beskrivning (valfri)"
              style={{
                flex: '2 1 200px',
                padding: '8px 10px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 14,
              }}
            />
            <Button onClick={cancelLink} startIcon={<CloseIcon />} style={{ textTransform: 'none' }}>
              Avbryt
            </Button>
            <Button
              onClick={addLink}
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              style={{ textTransform: 'none' }}
            >
              Lägg till
            </Button>
            {!!linkError && (
              <Box width="100%" color="orangered" fontWeight={700} fontSize={14}>
                {linkError}
              </Box>
            )}
          </div>
        )}
      </div>}
    </>
  );
}

interface IProductDocumentsProps {
  isCreatingNew?: boolean;
  state: IProduct;
  product?: IProduct;
  readOnly?: boolean;
  onChangeDocuments: (documents: IProductDocument[]) => void;
}

const ProductDocuments = ({ isCreatingNew, product, state, onChangeDocuments, readOnly }: IProductDocumentsProps) => {

  const keycloakCtx = useContext(KeycloakContext);
  const isSuperuser = keycloakCtx.keycloak.hasResourceRole('superuser');

  const documents: IProductDocument[] = state?.documents ?? [];
  const keepRef = useRef({ state });
  keepRef.current.state = state;

  const warnMessage = useMemo(() => {
    const savedDocuments = product?.documents || [];
    const currentDocuments = state?.documents || [];

    const savedMap = new Map(savedDocuments.map(document => [document.id, document]));
    const currentMap = new Map(currentDocuments.map(document => [document.id, document]));

    let hasAdded = false;
    let hasUpdated = false;
    let hasDeleted = false;

    for (const document of currentDocuments) {
      const savedDocument = savedMap.get(document.id);
      if (!savedDocument) hasAdded = true;
      else if (!isEqual(removeNilProperty(savedDocument), removeNilProperty(document))) hasUpdated = true;
    }

    for (const document of savedDocuments) {
      if (!currentMap.has(document.id)) hasDeleted = true;
    }

    const changes: string[] = [];
    if (hasAdded) changes.push('lagts till');
    if (hasUpdated) changes.push('uppdaterats');
    if (hasDeleted) changes.push('tagits bort');
    if (!changes.length) return '';

    return (
      <Box>
        {`Vissa dokument har ${changes.join('/')}.`}
        För att spara ändringarna,{' '}
        <span
          onClick={() => {
            scrollWithOffset(isCreatingNew ? 'create-product-a' : 'update-product-button');
          }}
          style={{ cursor: 'pointer', color: '#2196f3', textDecoration: 'underline' }}
        >
          klicka på {!isSuperuser ? 'SKICKA FÖR GRANSKNING' : isCreatingNew ? 'SKAPA PRODUKT' : 'UPPDATERA PRODUKT'}
        </span>.
      </Box>
    );
  }, [product?.documents, state?.documents, isCreatingNew, isSuperuser]);

  const updateDocument = (updated: IProductDocument) => {
    onChangeDocuments(documents.map(d => (d.id === updated.id ? updated : d)));
  };

  const removeDocument = (id: string) => {
    const doc = documents.find(d => d.id === id);
    if (doc?.type === 'file') URL.revokeObjectURL(doc.url);
    onChangeDocuments(documents.filter(d => d.id !== id));
  };

  const viewDocument = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <div id={PRODUCT_DOCUMENTS_SECTION_ID} style={{ width: '100%', marginTop: 16 }}>
      <InputLabel
        heading="Produktdokument"
        text={
          readOnly
            ? undefined
            : 'Ladda upp PDF, bilder eller andra dokument (säkerhetsdatablad, certifikat, manualer m.m.) — eller lägg till länkar till externa resurser.'
        }
      />

      {!readOnly && (
        <ProductDocumentsImport
          state={state}
          warnMessage={warnMessage}
          onAdd={(docs) => onChangeDocuments([...documents, ...docs])}
        />
      )}

      <div style={{ marginTop: 16 }}>
        {documents.length > 0 ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 'bold', marginBottom: 6, color: '#666' }}>
              Dokument ({documents.length})
            </div>
            {documents.map(doc => (
              <DocumentRow
                key={doc.id}
                product={product}
                doc={doc}
                onUpdate={updateDocument}
                onRemove={removeDocument}
                onView={viewDocument}
                readOnly={readOnly}
              />
            ))}
          </>
        ) : (
          <div
            style={{
              fontSize: 13,
              color: '#9e9e9e',
              fontStyle: 'italic',
              padding: '8px 0',
            }}
          >
            Inga dokument tillagda än.
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDocuments;
